import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useTheme } from '../../contextProviders';
import { useDrag, useDrop, DropTargetMonitor, DragSourceMonitor } from 'react-dnd';

interface ListItemType {
  id: string | number;
  disabled?: boolean;
  [key: string]: any;
}

interface ListItemProps {
  item: ListItemType;
  index: number;
  isSelected: boolean;
  setSelectedIndex: (index: number) => void;
  onClick: (params: { item: ListItemType; selectedIndex: number }) => void;
  renderer: (props: any) => React.ReactNode;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
}

interface ListMenuProps {
  items: ListItemType[];
  renderer: (props: any) => React.ReactNode;
  onClick?: (params: { item: ListItemType; selectedIndex: number }) => void;
  onReorder?: (items: ListItemType[]) => void;
}


const ListItem: React.FC<ListItemProps> = ({
  item,
  index,
  isSelected,
  setSelectedIndex,
  onClick,
  renderer,
  moveItem,
}) => {
  const { isDarkMode } = useTheme();
  const flex = 'flex flex-row justify-between items-center';
  const theme = !isDarkMode ? '!bg-white !text-black' : '!bg-indigo-dark !text-white';
  const onClickHandler = () => {
    setSelectedIndex(index);
    onClick({ item, selectedIndex: index });
    item.onClick?.({ ...item, index, isSelected });
  };


  const [{ isDragging }, drag] = useDrag({
    type: 'tools',
    item: { id: item.id, index },
    canDrag: () => Object.keys(item).length !== 0,
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'tools',
    hover: (draggedItem: { id: string | number; index: number }) => {
      if (draggedItem.index === index) return;
      moveItem(draggedItem.index, index);
      draggedItem.index = index;
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={classnames(flex, theme, {
        'cursor-move': !item.disabled,
        'cursor-pointer': !item.disabled,
        'ohif-disabled': item.disabled,
        'opacity-50': isDragging,
      })}
      onClick={onClickHandler}
      data-cy={item.id}
    >
      {renderer && renderer({ ...item, index, isSelected })}
    </div>
  );
};


const ListMenu: React.FC<ListMenuProps> = ({
  items = [],
  renderer,
  onClick = () => {},
  onReorder,
}) => {
  const { isDarkMode } = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [listItems, setListItems] = useState(items);

  const moveItem = (dragIndex, hoverIndex) => {
    const dragItem = listItems[dragIndex];
    const newItems = [...listItems];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, dragItem);
    setListItems(newItems);
    onReorder?.(newItems);
  };

  const [{ isOver }, drop] = useDrop({
    accept: 'tools',
    drop: () => ({}),
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  // Update local state when items prop changes
  React.useEffect(() => {
    setListItems(items);
  }, [items]);

  return (
    <div
      ref={drop}
      className={classnames(
        isDarkMode ? '!bg-secondary-dark !text-white' : '!bg-white !text-black',
        'flex flex-col gap-[4px] overflow-auto rounded-md p-1',
        { 'ring-2 ring-blue-500': isOver }
      )}
      style={{ maxHeight: '400px' }}
    >
      {listItems.map((item, index) => {
        return (
          <ListItem
            key={`ListItem${index}`}
            index={index}
            isSelected={selectedIndex === index}
            item={item}
            onClick={onClick}
            setSelectedIndex={setSelectedIndex}
            renderer={renderer}
            moveItem={moveItem}
          />
        );
      })}
    </div>
  );
};

// PropTypes are not needed when using TypeScript interfaces
// They're kept here for backward compatibility if needed by other parts of the codebase
ListMenu.propTypes = {
  items: PropTypes.array.isRequired as PropTypes.Validator<ListItemType[]>,
  renderer: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onReorder: PropTypes.func,
};

export default ListMenu;
